import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

// MUI Components
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem as MuiMenuItem,
  Popover,
  Switch,
  styled,
  Typography,
  SwitchProps,
  Tooltip,
  useTheme,
  keyframes,
} from '@mui/material';

import {
  Monitor as MonitorIcon,
  Code as CodeIcon,
  Key as KeyIcon,
  Video as VideoLibrary,
  Keyboard as KeyboardIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
} from 'lucide-react';

// Custom Components & Hooks
import { PageAndAccountMenu } from './OrgAndAccountMenu';
import MainMenuPanel from './MainMenuPanel';
import AppVersion from '../AppVersion';
import useCognito from '@/features/authentication/hooks/useCognito';
import { useKirbyMode } from '@/features/kirby-resources/hooks/useKirbyMode';
import { useUserStateContext } from '../UserStateProvider';
import { formatEndpoint } from '@/util/configureHttp';
import { getFirstLettersOfName, getRandomPastelColor } from '@/shared/services';
import { assetsBaseUrl } from '@/shared/constants';
import {
  Building2,
  Trophy,
  TvMinimalPlay,
  Users,
  UsersRound,
  ListStart,
  Share2,
  Radio,
  Clapperboard,
  Zap,
  Timer,
  ScrollText,
  SlidersHorizontal,
  LayoutGrid,
  Bot,
} from 'lucide-react';
import useKeyboardShortcutsDialog from '@/shared/hooks/useKeyboardShortcutsDialog';

interface NavigationMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

export interface MenuItem {
  path: string;
  icon: React.ReactNode;
  label: string;
  external?: boolean;
  href?: string;
  roles?: string[];
  onClick?: () => void;
  /** Rendered, but inert — the entry exists, this environment just cannot use it. */
  disabled?: boolean;
  /** Why it is disabled. Shown on hover; required for `disabled` to say anything. */
  disabledTooltip?: string;
}

export interface MenuSection {
  title: string;
  items: MenuItem[];
}

// Shared blur effect styling
export const blurredMenuStyles = {
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)', // For Safari
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.9)',
  border: '1px solid rgba(255, 255, 255, 0.10)',
  borderRadius: '8px',
};

// Create a styled version of Menu with blur effect
const BlurredMenu = styled(Menu)(() => ({
  '& .MuiPaper-root': {
    ...blurredMenuStyles,
    minWidth: '280px',
    maxHeight: '80vh',
    marginTop: '-2px',
    marginLeft: '-2px',
  },
  '& .MuiMenuItem-root': {
    fontSize: '0.7rem',
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.7rem',
  },
  '& .MuiTypography-overline': {
    fontSize: '0.65rem',
  },
}));

// Create a styled version of Popover with blur effect (for settings menu)
const BlurredPopover = styled(Popover)(() => ({
  '& .MuiPaper-root': {
    ...blurredMenuStyles,
    minWidth: '200px',
  },
  '& .MuiMenuItem-root': {
    fontSize: '0.7rem',
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.7rem',
  },
  '& .MuiListItemText-secondary': {
    fontSize: '0.65rem',
  },
}));

// Staggered slide-in animation (left to right)
const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-12px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Animated menu item wrapper
const AnimatedMenuItem = styled(MuiMenuItem)<{ delay?: number }>(({ delay = 0 }) => ({
  opacity: 0,
  animation: `${slideInFromLeft} 0.12s ease-out forwards`,
  animationDelay: `${delay}ms`,
}));

// Animated divider
const AnimatedDivider = styled(Divider)<{ delay?: number }>(({ delay = 0 }) => ({
  opacity: 0,
  animation: `${slideInFromLeft} 0.1s ease-out forwards`,
  animationDelay: `${delay}ms`,
}));

// Animated section header
const AnimatedSectionHeader = styled(Box)<{ delay?: number }>(({ delay = 0 }) => ({
  opacity: 0,
  animation: `${slideInFromLeft} 0.1s ease-out forwards`,
  animationDelay: `${delay}ms`,
}));

function truncateWithEllipsis(text: string, maxLength: number) {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + '...';
}

const STROKE_WIDTH = 0.6;

const NavigationMenu: React.FC<NavigationMenuProps> = ({ anchorEl, onClose }) => {
  // Hooks
  const location = useLocation();
  const cognito = useCognito();

  const {
    clipExportApi,
    clipLibrary,
    enableWscStreams,
    enableSimuliveAndTestLab,
    zeroTouchClipping,
    horizontalMainMenu,
  } = useFlags();
  const { hasRole, userProfile, actions } = useUserStateContext();
  // Disabled rather than hidden: an operator looking for Resources should find
  // out that the environment runs no Kirby services, not that the page vanished.
  const { mode: kirbyMode } = useKirbyMode();

  // State
  const [settingsAnchorEl, setSettingsAnchorEl] = React.useState<HTMLElement | null>(null);
  const [userEmail, setUserEmail] = React.useState('');
  const [avatarBgColor, setAvatarBgColor] = React.useState('#333');

  // Constants
  const apiDocumentationUrl = formatEndpoint('/v1.0/documentation');
  const settingsMenuOpen = Boolean(settingsAnchorEl);
  const menuId = settingsMenuOpen ? 'settings-popover' : undefined;
  const open = Boolean(anchorEl);
  const keyboardShortcutsDialog = useKeyboardShortcutsDialog();

  const menuSections: MenuSection[] = React.useMemo(
    () => [
      {
        title: 'Watch',
        items: [
          {
            path: '/games',
            icon: <TvMinimalPlay strokeWidth={STROKE_WIDTH} />,
            label: 'Games',
            roles: ['menu:games'],
          },
          {
            path: '/clips',
            icon: <VideoLibrary strokeWidth={STROKE_WIDTH} />,
            label: 'Clips',
            roles: ['menu:clips'],
          },
          ...(clipLibrary
            ? [
                {
                  path: '/clip-library',
                  icon: <LayoutGrid strokeWidth={STROKE_WIDTH} />,
                  label: 'Clip Library',
                  roles: ['menu:clips'],
                },
              ]
            : []),
          ...(enableWscStreams
            ? [
                {
                  path: '/streams',
                  icon: <Radio strokeWidth={STROKE_WIDTH} />,
                  label: 'SRT Streams',
                  roles: ['menu:games:wsc-streams'],
                },
              ]
            : []),
          {
            path: '/resources',
            icon: <Zap strokeWidth={STROKE_WIDTH} />,
            label: 'Resources',
            roles: ['menu:games:resources'],
            disabled: kirbyMode === 'off',
            disabledTooltip: 'Kirby is disabled.',
          },
          ...(enableSimuliveAndTestLab
            ? [
                {
                  path: '/simulive',
                  icon: <Radio strokeWidth={STROKE_WIDTH} />,
                  label: 'Simulive and Test lab',
                  roles: ['menu:games:simulive'],
                },
              ]
            : []),
        ],
      },
      {
        title: 'Help & Support',
        items: [
          {
            path: 'keyboard-shortcuts',
            icon: <KeyboardIcon strokeWidth={STROKE_WIDTH} />,
            label: 'Keyboard Shortcuts',
            onClick: () => keyboardShortcutsDialog.show(),
          },
          {
            path: `${assetsBaseUrl}/user-guide.pdf`,
            icon: <InfoIcon strokeWidth={STROKE_WIDTH} />,
            label: 'CXVH User Guide',
            external: true,
            href: `${assetsBaseUrl}/user-guide.pdf`,
          },
        ],
      },
      ...(clipExportApi
        ? [
            {
              title: 'Clip Export API',
              items: [
                {
                  path: apiDocumentationUrl,
                  icon: <CodeIcon strokeWidth={STROKE_WIDTH} />,
                  label: 'API Docs',
                  external: true,
                  href: apiDocumentationUrl,
                  roles: ['menu:api'],
                },
                {
                  path: '/api-key',
                  icon: <KeyIcon strokeWidth={STROKE_WIDTH} />,
                  label: 'API Key',
                  roles: ['menu:api'],
                },
              ],
            },
          ]
        : []),
      {
        title: 'MCP',
        items: [
          {
            path: '/mcp',
            icon: <Bot strokeWidth={STROKE_WIDTH} />,
            label: 'MCP Server',
            roles: ['menu:mcp'],
          },
        ],
      },
      {
        title: 'Manage Organization',
        items: [
          {
            path: '/org-management',
            icon: <SettingsIcon strokeWidth={STROKE_WIDTH} />,
            label: 'Organization Settings',
            roles: ['menu:manage:org'],
          },
          {
            path: '/manage-users',
            icon: <UsersRound strokeWidth={STROKE_WIDTH} />,
            label: 'Manage Users',
            roles: ['menu:manage:org'],
          },
          {
            path: '/clip-dispatch',
            icon: <Share2 strokeWidth={STROKE_WIDTH} />,
            label: 'Clip Dispatch',
            roles: ['menu:manage:dispatch'],
          },
          // Shadow launch (C360-2813): zeroTouchClipping hides the entry point while the
          // rules editor is being validated. The /automated-clipping route stays
          // registered, so anyone verifying the feature can reach it directly, and the
          // API enforces the role independently.
          ...(zeroTouchClipping
            ? [
                {
                  path: '/automated-clipping',
                  icon: <Clapperboard strokeWidth={STROKE_WIDTH} />,
                  label: 'Automated Clipping',
                  roles: ['menu:manage:org'],
                },
              ]
            : []),
          {
            path: '/manage-groups',
            icon: <Users strokeWidth={STROKE_WIDTH} />,
            label: 'Manage Groups',
            roles: ['menu:manage:groups'],
          },
          {
            path: '/manage-departments',
            icon: <Building2 strokeWidth={STROKE_WIDTH} />,
            label: 'Manage Departments',
            roles: ['menu:manage:departments'],
          },
        ],
      },
      {
        title: 'Manage CXVH',
        items: [
          {
            path: '/manage-games',
            icon: <Trophy strokeWidth={STROKE_WIDTH} />,
            label: 'Manage Games',
            roles: ['menu:admin:games'],
          },
          {
            path: '/manage-q',
            icon: <ListStart strokeWidth={STROKE_WIDTH} />,
            label: 'Clip Export Q',
            roles: ['menu:admin:queues'],
          },
          {
            path: '/manage-crons',
            icon: <Timer strokeWidth={STROKE_WIDTH} />,
            label: 'Cron Jobs',
            roles: ['menu:admin:crons'],
          },
          {
            path: '/audit-logs',
            icon: <ScrollText strokeWidth={STROKE_WIDTH} />,
            label: 'Audit Logs',
            roles: ['menu:admin:auditlogs'],
          },
          {
            path: '/transcoding-profiles',
            icon: <Clapperboard strokeWidth={STROKE_WIDTH} />,
            label: 'Transcoding Profiles',
            roles: ['menu:admin:profiles'],
          },
          {
            path: '/manage-sessions',
            icon: <MonitorIcon strokeWidth={STROKE_WIDTH} />,
            label: 'Manage Sessions',
            roles: ['menu:admin:sessions'],
          },
          {
            path: '/manage-configs',
            icon: <SlidersHorizontal strokeWidth={STROKE_WIDTH} />,
            label: 'Configs',
            roles: ['menu:admin:configs'],
          },
        ],
      },
    ],
    [
      apiDocumentationUrl,
      clipExportApi,
      clipLibrary,
      enableWscStreams,
      enableSimuliveAndTestLab,
      zeroTouchClipping,
      kirbyMode,
    ],
  );

  // Effects
  React.useEffect(() => {
    if (cognito.userAttributes?.email) {
      const email = cognito.userAttributes.email;
      setUserEmail(email);
      setAvatarBgColor(getRandomPastelColor(email.charAt(0), email.charAt(1)));
    }
  }, [cognito.authenticated, cognito.userAttributes?.email]);

  // Event Handlers
  const handleCloseSettingsMenu = () => setSettingsAnchorEl(null);
  const handleOpenSettingsMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSettingsAnchorEl(event.currentTarget);
  };
  const handleMenuItemClick = () => {
    onClose();
  };

  // Animation timing
  const STAGGER_DELAY = 20; // ms between each item
  const itemIndexRef = React.useRef(0);

  // Reset animation index when menu opens
  React.useEffect(() => {
    if (open) {
      itemIndexRef.current = 0;
    }
  }, [open]);

  // Render Methods
  const renderMenuItem = React.useCallback(
    (
      {
        path,
        icon,
        label,
        external,
        href,
        roles = [],
        onClick,
        disabled,
        disabledTooltip,
      }: MenuItem,
      itemIndex: number,
    ) => {
      if (!hasRole(roles)) return null;

      const delay = itemIndex * STAGGER_DELAY;
      const commonProps = {
        delay,
        sx: {
          paddingLeft: 4,
          paddingRight: 4,
          minHeight: '48px',
        },
      };

      if (disabled) {
        // The span is load-bearing: MUI strips pointer events from a disabled
        // MenuItem, so a Tooltip anchored straight to it never fires.
        return (
          <Tooltip key={path} title={disabledTooltip ?? ''} placement="right">
            <span>
              <AnimatedMenuItem {...commonProps} disabled sx={{ ...commonProps.sx, width: '100%' }}>
                <ListItemIcon sx={{ minWidth: '40px' }}>{icon}</ListItemIcon>
                <ListItemText primary={label} />
              </AnimatedMenuItem>
            </span>
          </Tooltip>
        );
      }

      if (external) {
        return (
          <AnimatedMenuItem
            key={path}
            {...commonProps}
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleMenuItemClick}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>{icon}</ListItemIcon>
            <ListItemText primary={label} />
          </AnimatedMenuItem>
        );
      }

      if (onClick) {
        return (
          <AnimatedMenuItem
            key={path}
            {...commonProps}
            onClick={() => {
              onClick();
              handleMenuItemClick();
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>{icon}</ListItemIcon>
            <ListItemText primary={label} />
          </AnimatedMenuItem>
        );
      }

      return (
        <AnimatedMenuItem
          key={path}
          {...commonProps}
          component={Link}
          to={path}
          selected={location.pathname === path}
          onClick={handleMenuItemClick}
        >
          <ListItemIcon sx={{ minWidth: '40px' }}>{icon}</ListItemIcon>
          <ListItemText primary={label} />
        </AnimatedMenuItem>
      );
    },
    [hasRole, location.pathname, handleMenuItemClick],
  );

  // Track global item index across all sections for proper stagger
  const getNextIndex = () => itemIndexRef.current++;

  const renderMenuSection = React.useCallback(
    ({ title, items }: MenuSection, sectionIndex: number) => {
      const visibleItems = items.filter((item) => hasRole(item.roles || []));
      if (visibleItems.length === 0) return null;

      const elements: React.ReactNode[] = [];

      // Divider (except for first section)
      if (sectionIndex > 0) {
        const dividerIndex = getNextIndex();
        elements.push(
          <AnimatedDivider
            key={`divider-${sectionIndex}`}
            delay={dividerIndex * STAGGER_DELAY}
            sx={{ my: 1 }}
          />,
        );
      }

      // Section header
      const headerIndex = getNextIndex();
      elements.push(
        <AnimatedSectionHeader
          key={`header-${sectionIndex}`}
          delay={headerIndex * STAGGER_DELAY}
          sx={{ paddingLeft: 2, paddingTop: 1, paddingBottom: 0.5 }}
        >
          <Typography variant="overline" sx={{ fontSize: '0.7rem', opacity: 0.7 }}>
            {title}
          </Typography>
        </AnimatedSectionHeader>,
      );

      // Menu items
      visibleItems.forEach((item) => {
        const itemIndex = getNextIndex();
        const rendered = renderMenuItem(item, itemIndex);
        if (rendered) elements.push(rendered);
      });

      return elements;
    },
    [hasRole, renderMenuItem],
  );

  const theme = useTheme();

  const switchHeight = 19;
  const switchWidth = 35;
  const switchColor = theme.palette.primary.main;
  const IOSSwitch = styled((props: SwitchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: switchWidth,
    height: switchHeight,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transition: theme.transitions.create(['transform'], {
        duration: 400,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }),
      '&.Mui-checked': {
        transform: `translateX(${switchWidth - switchHeight}px)`,
        color: '#fff',
        '& .MuiSwitch-thumb': {
          boxShadow: `0 3px 8px 0 ${switchColor}66`,
        },
        '& + .MuiSwitch-track': {
          backgroundColor: switchColor,
          opacity: 1,
          border: 0,
          ...theme.applyStyles('dark', {
            backgroundColor: switchColor,
          }),
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: switchColor,
        border: '6px solid #fff',
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.grey[100],
        ...theme.applyStyles('dark', {
          color: theme.palette.grey[600],
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.7,
        ...theme.applyStyles('dark', {
          opacity: 0.3,
        }),
      },
      // Hover effect
      '&:hover .MuiSwitch-thumb': {
        transform: 'scale(1.05)',
        boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.15)',
      },
      '&.Mui-checked:hover .MuiSwitch-thumb': {
        boxShadow: `0 3px 10px 0 ${switchColor}80`,
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: switchHeight - 4,
      height: switchHeight - 4,
      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.12)',
      transition: theme.transitions.create(['transform', 'box-shadow'], {
        duration: 300,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }),
    },
    '& .MuiSwitch-track': {
      borderRadius: switchHeight / 2,
      backgroundColor: '#E9E9EA',
      opacity: 1,
      transition: theme.transitions.create(['background-color', 'opacity'], {
        duration: 400,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }),
      ...theme.applyStyles('dark', {
        backgroundColor: '#39393D',
      }),
    },
  }));

  if (horizontalMainMenu) {
    return (
      <MainMenuPanel
        anchorEl={anchorEl}
        onClose={onClose}
        sections={menuSections}
        userEmail={userEmail}
        avatarBgColor={avatarBgColor}
      />
    );
  }

  return (
    <>
      <BlurredMenu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transitionDuration={0}
        slotProps={{
          paper: {
            TransitionProps: { timeout: 0 },
          },
        }}
      >
        {/* User Section */}
        <AnimatedMenuItem
          onClick={handleOpenSettingsMenu}
          delay={0}
          sx={{ paddingLeft: 2, paddingRight: 4 }}
        >
          <ListItemIcon sx={{ minWidth: '40px', marginRight: 1 }}>
            <Avatar
              src={userProfile?.profilePicturePath ?? undefined}
              sx={{ bgcolor: avatarBgColor, width: 32, height: 32 }}
              variant="rounded"
            >
              {getFirstLettersOfName(userEmail)}
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={truncateWithEllipsis(userEmail, 30)}
            slotProps={{ primary: { variant: 'body2' } }}
          />
        </AnimatedMenuItem>
        <AnimatedDivider delay={STAGGER_DELAY} sx={{ my: 1 }} />

        {/* Menu Sections - reset index after user section */}
        {open &&
          (() => {
            itemIndexRef.current = 2;
            return null;
          })()}
        {menuSections.map(renderMenuSection)}

        {false && (
          <>
            <Divider sx={{ my: 1 }} />
            <MuiMenuItem
              sx={{
                paddingLeft: 4,
                paddingRight: 4,
                minHeight: '48px',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <ListItemText primary="Use arena timezone" />
              <IOSSwitch
                size="small"
                color="warning"
                checked={userProfile?.useVenueTimezone ?? true}
                onChange={async (event) => {
                  try {
                    await actions.updateUseVenueTimezone(event.target.checked);
                  } catch (error) {
                    console.error('Failed to update timezone preference', error);
                  }
                }}
                slotProps={{ input: { 'aria-label': 'Use arena timezone' } }}
              />
            </MuiMenuItem>
          </>
        )}

        {/* Version — always the last section */}
        <AnimatedDivider delay={getNextIndex() * STAGGER_DELAY} sx={{ my: 1 }} />
        <AnimatedSectionHeader
          delay={getNextIndex() * STAGGER_DELAY}
          sx={{ paddingLeft: 2, paddingBottom: 0.5 }}
        >
          <AppVersion />
        </AnimatedSectionHeader>
      </BlurredMenu>

      <BlurredPopover
        id={menuId}
        open={settingsMenuOpen}
        anchorEl={settingsAnchorEl}
        onClose={handleCloseSettingsMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transitionDuration={0}
        slotProps={{
          transition: { timeout: 0 },
        }}
      >
        <PageAndAccountMenu handleClose={handleCloseSettingsMenu} />
      </BlurredPopover>
    </>
  );
};

export default NavigationMenu;
